import { useEffect, useState } from 'react'
import { useApiClient } from '@/utils/api/ApiClient'
import { TradingAccountsApiClient } from '@/utils/api/tradingAccountsApi'
import { DataResult, process, State } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridDataStateChangeEvent,
  GridExpandChangeEvent,
} from '@progress/kendo-react-grid'
import { PermissionDto } from 'api/Identity/Types/PermissionDto'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { PlatformStatisticsDto } from 'api/Trading-Accounts/Types/PlatformStatisticsDto'
import { Spinner } from 'components/Spinner'
import { PlatformStatisticsKey } from 'store/transactions/transaction.types'
import { formatLocalDateUTC } from 'utils/date'
import { hasPermission } from 'utils/permissionsValidator'
import { TableWithExport } from 'utils/table/withExport'

import walletsColumn from '../../../Records/Wallets/walletsColumn'
import { normalizeStatistics } from './normalizer'
import { clientDetailsStatisticsColumns } from './transactionsStatisticColumns'

interface StateProps {
  client: {
    id: string
    name: string
  }
}

const useTransactionsStatisticOverview = ({ client }: StateProps) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const [isLoading, setLoading] = useState<boolean>(false)
  const [statistics, setStatistics] = useState<PlatformStatisticsDto[] | null>(null)

  const fetchData = async (clientId: string) => {
    try {
      setLoading(true)
      const response = await tradingAccountsApiClient.getTradingAccountPlatformStatistics(clientId)
      setStatistics(normalizeStatistics(response))
      return setLoading(false)
    } catch (error) {
      console.error(error)
      return setLoading(false)
    }
  }

  useEffect(() => {
    ;(async () => {
      await fetchData(client.id)
    })()
  }, [client]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isLoading: isLoading || !statistics,
    statistics,
  }
}

const createDataState = (state: State, statistics: PlatformStatisticsDto[]) => ({
  result: process(
    statistics.map((statistic: PlatformStatisticsDto) => ({
      ...statistic,
      [PlatformStatisticsKey.FIRST_TRADE]: statistic?.[PlatformStatisticsKey.FIRST_TRADE]
        ? formatLocalDateUTC(statistic[PlatformStatisticsKey.FIRST_TRADE] || '', true)
        : undefined,
      [PlatformStatisticsKey.LAST_TRADE]: statistic?.[PlatformStatisticsKey.LAST_TRADE]
        ? formatLocalDateUTC(statistic[PlatformStatisticsKey.LAST_TRADE] || '', true)
        : undefined,
      [PlatformStatisticsKey.FIRST_DEPOSIT]: statistic?.[PlatformStatisticsKey.FIRST_DEPOSIT]
        ? formatLocalDateUTC(statistic[PlatformStatisticsKey.FIRST_DEPOSIT] || '', true)
        : undefined,
      [PlatformStatisticsKey.LAST_DEPOSIT]: statistic?.[PlatformStatisticsKey.LAST_DEPOSIT]
        ? formatLocalDateUTC(statistic[PlatformStatisticsKey.LAST_DEPOSIT] || '', true)
        : undefined,
      [PlatformStatisticsKey.FIRST_WITHDRAWAL]: statistic?.[PlatformStatisticsKey.FIRST_WITHDRAWAL]
        ? formatLocalDateUTC(statistic[PlatformStatisticsKey.FIRST_WITHDRAWAL] || '', true)
        : undefined,
      [PlatformStatisticsKey.LAST_WITHDRAWAL]: statistic?.[PlatformStatisticsKey.LAST_WITHDRAWAL]
        ? formatLocalDateUTC(statistic[PlatformStatisticsKey.LAST_WITHDRAWAL] || '', true)
        : undefined,
    })),
    state
  ),
  dataState: state,
})

interface TransactionsStatisticOverviewModalViewProps {
  statistics: PlatformStatisticsDto[] | null
  permissions?: PermissionDto[]
}

export const TransactionsStatisticOverviewModalView: React.FC<
  TransactionsStatisticOverviewModalViewProps
> = (props) => {
  const statistics = props.statistics || []
  const [dataState, setDataState] = useState<{ result: DataResult; dataState: State }>(
    createDataState({ group: [{ field: 'currencyId' }] }, statistics)
  )

  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    if (statistics) {
      setDataState(createDataState(event.dataState, statistics))
    }
  }

  const pageChange = async () => {
    if (statistics) {
      setDataState(createDataState({ ...dataState.dataState }, statistics))
    }
  }

  const handleExpandChange = (event: GridExpandChangeEvent) => {
    event.dataItem[event.target.props.expandField as string] = event.value
    setDataState({
      result: Object.assign({}, dataState.result),
      dataState: dataState.dataState,
    })
  }

  return (
    <div style={{ margin: '0.75rem' }}>
      <TableWithExport
        data={statistics}
        columns={walletsColumn}
        hasPermissions={hasPermission(
          props.permissions || [],
          PermissionsEnum.CanExportTransactionsModule
        )}
      >
        <Grid
          {...dataState.dataState}
          filterable={false}
          sortable
          groupable
          data={dataState.result.data}
          onPageChange={pageChange}
          onDataStateChange={handleDataStateChange}
          onExpandChange={handleExpandChange}
          style={{ zIndex: 0 }}
          expandField='expanded'
        >
          {clientDetailsStatisticsColumns({ statistics })
            .filter((c) => c.show)
            .map((column, idx) => (
              <Column key={idx + 2} {...column} />
            ))}
        </Grid>
      </TableWithExport>
    </div>
  )
}

export const TransactionsStatisticOverview: React.FC<StateProps> = ({ client }: StateProps) => {
  const { isLoading, statistics } = useTransactionsStatisticOverview({ client })

  if (isLoading) return <Spinner />
  return <TransactionsStatisticOverviewModalView statistics={statistics} />
}
